<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <h2 class="font-semibold text-center mb-7">Informations KYC</h2>

      <!-- Mode consultation -->
      <div v-if="!isEditing" class="kyc-consultation">
        <div class="kyc-section mb-6">
          <h4 class="text-primary-color mb-4">Photo de profil</h4>
          <div class="kyc-photo-display">
            <img 
              :src="kycData.photoProfilUrl || '/public/assets/users/300w/user2.jpg'" 
              class="kyc-photo-preview rounded-20"
              alt="Photo de profil"
            />
          </div>
        </div>

        <div class="kyc-section mb-6">
          <h4 class="text-primary-color mb-4">Pièce d'identité</h4>
          <div class="kyc-info-row">
            <span class="kyc-label">Type :</span>
            <span class="kyc-value">{{ formatPieceType(kycData.pieceIdentiteType) }}</span>
          </div>
          <div class="kyc-info-row">
            <span class="kyc-label">Numéro :</span>
            <span class="kyc-value">{{ kycData.numeroPiece || 'Non renseigné' }}</span>
          </div>
          <div class="kyc-documents mt-4">
            <div v-if="kycData.pieceRectoUrl" class="kyc-document-item">
              <img src="/public/icons/icon-idcard-recto.svg" class="size-medium" alt="" />
              <span>Pièce recto</span>
              <a :href="kycData.pieceRectoUrl" target="_blank" class="btn-sm">Voir</a>
            </div>
            <div v-if="kycData.pieceVersoUrl" class="kyc-document-item">
              <img src="/public/icons/icon-idcard-verso.svg" class="size-medium" alt="" />
              <span>Pièce verso</span>
              <a :href="kycData.pieceVersoUrl" target="_blank" class="btn-sm">Voir</a>
            </div>
          </div>
        </div>

        <div class="kyc-section mb-6">
          <h4 class="text-primary-color mb-4">Bulletins de salaire</h4>
          <div class="kyc-documents">
            <div v-if="kycData.bulletin1Url" class="kyc-document-item">
              <span class="kyc-doc-number">.1</span>
              <span>Bulletin 1</span>
              <a :href="kycData.bulletin1Url" target="_blank" class="btn-sm">Télécharger</a>
            </div>
            <div v-if="kycData.bulletin2Url" class="kyc-document-item">
              <span class="kyc-doc-number">.2</span>
              <span>Bulletin 2</span>
              <a :href="kycData.bulletin2Url" target="_blank" class="btn-sm">Télécharger</a>
            </div>
            <div v-if="kycData.bulletin3Url" class="kyc-document-item">
              <span class="kyc-doc-number">.3</span>
              <span>Bulletin 3</span>
              <a :href="kycData.bulletin3Url" target="_blank" class="btn-sm">Télécharger</a>
            </div>
          </div>
        </div>

        <button 
          @click="startEditing" 
          class="btn lg w-100 flex-center mt-5"
        >
          <img src="/public/icons/settings-sliders.svg" class="btn-icon" alt="" />
          <span>Modifier mes informations</span>
        </button>
      </div>

      <!-- Mode édition -->
      <div v-else class="kyc-edition">
        <div v-if="editStep === 1">
          <h3 class="font-semibold text-center mb-6">Identité</h3>
          <form class="form" @submit.prevent="goToNextStep">
            <!-- Photo de profil -->
            <label 
              for="file-upload-photo" 
              class="custom-upload"
              :class="{ active: formData.photoProfil }"
            >
              <div class="custom-upload-st2">
                <input 
                  id="file-upload-photo" 
                  type="file"
                  accept="image/*"
                  @change="handlePhotoUpload"
                >
                <div 
                  class="s5 custom-upload-img" 
                  :style="photoPreviewStyle"
                >
                  <img 
                    v-if="!photoPreview" 
                    src="/public/icons/icon-photo.svg" 
                    width="45%" 
                    class="icon-white" 
                  />
                </div>
                <div class="s10">
                  <span class="custom-upload-text">Photo de profil</span><br>
                  <span class="custom-upload-text-sm">
                    {{ formData.photoProfil ? formData.photoProfil.name : 'Charger la photo' }}
                  </span>
                </div>
                <div class="s5 right">
                  <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
                </div>
              </div>
            </label>

            <div class="h-10"></div>

            <!-- Type et numéro de pièce -->
            <div class="form-field-split active rounded-20 mt-5 my-6">
              <div class="form-field-floating full lg w-40">
                <select 
                  class="form-input-select" 
                  v-model="formData.pieceIdentiteType"
                >
                  <option value="">Type de pièce</option>
                  <option value="cni">CNI</option>
                  <option value="passport">Passeport</option>
                  <option value="permis">Permis de conduire</option>
                  <option value="carte_sejour">Carte de séjour</option>
                </select>
                <label>Pièce d'identité <b class="color-danger">*</b></label>
              </div>
              <hr class="hr" />
              <div class="form-field-floating lg reactive w-60">
                <input 
                  type="text" 
                  class="form-input" 
                  v-model="formData.numeroPiece"
                  placeholder=""
                >
                <label>N° de pièce d'identité <b class="color-danger">*</b></label>
              </div>
            </div>

            <!-- Pièce Recto -->
            <label 
              for="file-upload-recto" 
              class="custom-upload my-5"
              :class="{ active: formData.pieceRecto }"
            >
              <div class="custom-upload-st2">
                <input 
                  id="file-upload-recto" 
                  type="file"
                  accept="image/*,.pdf"
                  @change="handleRectoUpload"
                >
                <div 
                  class="s5 custom-upload-img" 
                  :style="rectoPreviewStyle"
                >
                  <img 
                    v-if="!rectoPreview" 
                    src="/public/icons/icon-idcard-recto.svg" 
                    width="45%" 
                    class="icon-white" 
                  />
                </div>
                <div class="s10">
                  <span class="custom-upload-text uppercase">Pièce recto</span> 
                  <b class="color-danger">*</b><br>
                  <span class="custom-upload-text-sm">
                    {{ formData.pieceRecto ? formData.pieceRecto.name : 'Charger l\'image' }}
                  </span>
                </div>
                <div class="s5 right">
                  <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
                </div>
              </div>
            </label>

            <!-- Pièce Verso -->
            <label 
              for="file-upload-verso" 
              class="custom-upload"
              :class="{ active: formData.pieceVerso }"
            >
              <div class="custom-upload-st2">
                <input 
                  id="file-upload-verso" 
                  type="file"
                  accept="image/*,.pdf"
                  @change="handleVersoUpload"
                >
                <div 
                  class="s5 custom-upload-img" 
                  :style="versoPreviewStyle"
                >
                  <img 
                    v-if="!versoPreview" 
                    src="/public/icons/icon-idcard-verso.svg" 
                    width="45%" 
                    class="icon-white" 
                  />
                </div>
                <div class="s10">
                  <span class="custom-upload-text uppercase">Pièce verso</span> 
                  <b class="color-danger">*</b><br>
                  <span class="custom-upload-text-sm">
                    {{ formData.pieceVerso ? formData.pieceVerso.name : 'Charger l\'image' }}
                  </span>
                </div>
                <div class="s5 right">
                  <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
                </div>
              </div>
            </label>

            <button type="submit" class="btn lg w-100 flex-center mt-5">
              Suivant
            </button>
          </form>
        </div>

        <div v-if="editStep === 2">
          <h3 class="font-semibold text-center mb-6">Bulletins de salaire</h3>
          <form class="form" @submit.prevent="handleSubmitKyc">
            <!-- Bulletin 3 -->
            <label 
              for="file-upload-bulletin3" 
              class="custom-upload my-5"
              :class="{ active: formData.bulletin3 }"
            >
              <div class="custom-upload-st2">
                <input 
                  id="file-upload-bulletin3" 
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  @change="(e) => handleBulletinUpload(e, 3)"
                >
                <div class="s4 custom-upload-img">.3</div>
                <div class="s12">
                  <span class="custom-upload-text">Bulletin de salaire (3)</span> 
                  <b class="color-danger">*</b><br>
                  <span class="custom-upload-text-sm">
                    {{ formData.bulletin3 ? formData.bulletin3.name : 'Charger le doc' }}
                  </span>
                </div>
                <div class="s4 right">
                  <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
                </div>
              </div>
            </label>

            <!-- Bulletin 2 -->
            <label 
              for="file-upload-bulletin2" 
              class="custom-upload my-5"
              :class="{ active: formData.bulletin2 }"
            >
              <div class="custom-upload-st2">
                <input 
                  id="file-upload-bulletin2" 
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  @change="(e) => handleBulletinUpload(e, 2)"
                >
                <div class="s4 custom-upload-img">.2</div>
                <div class="s12">
                  <span class="custom-upload-text">Bulletin de salaire (2)</span> 
                  <b class="color-danger">*</b><br>
                  <span class="custom-upload-text-sm">
                    {{ formData.bulletin2 ? formData.bulletin2.name : 'Charger le doc' }}
                  </span>
                </div>
                <div class="s4 right">
                  <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
                </div>
              </div>
            </label>

            <!-- Bulletin 1 -->
            <label 
              for="file-upload-bulletin1" 
              class="custom-upload my-5"
              :class="{ active: formData.bulletin1 }"
            >
              <div class="custom-upload-st2">
                <input 
                  id="file-upload-bulletin1" 
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  @change="(e) => handleBulletinUpload(e, 1)"
                >
                <div class="s4 custom-upload-img">.1</div>
                <div class="s12">
                  <span class="custom-upload-text">Bulletin de salaire (1)</span> 
                  <b class="color-danger">*</b><br>
                  <span class="custom-upload-text-sm">
                    {{ formData.bulletin1 ? formData.bulletin1.name : 'Charger le doc' }}
                  </span>
                </div>
                <div class="s4 right">
                  <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
                </div>
              </div>
            </label>

            <button 
              type="submit" 
              class="btn lg w-100 flex-center mt-5"
              :disabled="isSubmitting"
            >
              <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
              <span>{{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}</span>
            </button>

            <a @click.prevent="goToPreviousStep" class="cursor-pointer mt-4">
              <h5 class="flex-center gap-4 text-primary-color text-center">
                <img src="/public/icons/icon-back.svg" class="size-medium" alt="" /> 
                Étape précédente
              </h5>
            </a>
          </form>
        </div>
      </div>

      <a v-if="!isEditing" @click.prevent="goBack" class="cursor-pointer mt-5">
        <h5 class="flex-center gap-4 text-primary-color text-center">
          <img src="/public/icons/icon-back.svg" class="size-medium" alt="" /> 
          Retour au profil
        </h5>
      </a>

      <a v-else @click.prevent="cancelEditing" class="cursor-pointer mt-4">
        <h5 class="flex-center gap-4 text-grey-600 text-center">
          Annuler
        </h5>
      </a>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/authService'
import { showAlert } from '@/utils/swal'

interface KycData {
  photoProfilUrl: string
  pieceIdentiteType: string
  numeroPiece: string
  pieceRectoUrl: string
  pieceVersoUrl: string
  bulletin1Url: string
  bulletin2Url: string
  bulletin3Url: string
}

interface FormData {
  photoProfil: File | null
  pieceIdentiteType: string
  numeroPiece: string
  pieceRecto: File | null
  pieceVerso: File | null
  bulletin1: File | null
  bulletin2: File | null
  bulletin3: File | null
}

export default defineComponent({
  name: 'FrontofficeKyc',
  setup() {
    const router = useRouter()
    const isEditing = ref(false)
    const editStep = ref(1)
    const isSubmitting = ref(false)

    const kycData = reactive<KycData>({
      photoProfilUrl: '',
      pieceIdentiteType: '',
      numeroPiece: '',
      pieceRectoUrl: '',
      pieceVersoUrl: '',
      bulletin1Url: '',
      bulletin2Url: '',
      bulletin3Url: ''
    })

    const formData = reactive<FormData>({
      photoProfil: null,
      pieceIdentiteType: '',
      numeroPiece: '',
      pieceRecto: null,
      pieceVerso: null,
      bulletin1: null,
      bulletin2: null,
      bulletin3: null
    })

    const photoPreview = ref('')
    const rectoPreview = ref('')
    const versoPreview = ref('')

    const photoPreviewStyle = computed(() => {
      return photoPreview.value 
        ? { backgroundImage: `url(${photoPreview.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {}
    })

    const rectoPreviewStyle = computed(() => {
      return rectoPreview.value 
        ? { backgroundImage: `url(${rectoPreview.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {}
    })

    const versoPreviewStyle = computed(() => {
      return versoPreview.value 
        ? { backgroundImage: `url(${versoPreview.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {}
    })

    onMounted(async () => {
      await loadKycData()
    })

    const loadKycData = async () => {
      try {
        const response = await authService.getProfile()
        if (response.status === 0 && response.data) {
          const user = response.data
          kycData.photoProfilUrl = user.photo_url || user.photo || ''
          kycData.pieceIdentiteType = user.piece_identite_type || ''
          kycData.numeroPiece = user.numero_piece || ''
          kycData.pieceRectoUrl = user.piece_recto || ''
          kycData.pieceVersoUrl = user.piece_verso || ''
          kycData.bulletin1Url = user.bulletin1 || ''
          kycData.bulletin2Url = user.bulletin2 || ''
          kycData.bulletin3Url = user.bulletin3 || ''
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données KYC:', error)
      }
    }

    const formatPieceType = (type: string): string => {
      const types: Record<string, string> = {
        'cni': 'CNI',
        'passport': 'Passeport',
        'permis': 'Permis de conduire',
        'carte_sejour': 'Carte de séjour'
      }
      return types[type] || 'Non renseigné'
    }

    const startEditing = () => {
      isEditing.value = true
      editStep.value = 1
      // Pré-remplir le formulaire avec les données existantes
      formData.pieceIdentiteType = kycData.pieceIdentiteType
      formData.numeroPiece = kycData.numeroPiece
    }

    const cancelEditing = () => {
      isEditing.value = false
      editStep.value = 1
      // Réinitialiser le formulaire
      formData.photoProfil = null
      formData.pieceRecto = null
      formData.pieceVerso = null
      formData.bulletin1 = null
      formData.bulletin2 = null
      formData.bulletin3 = null
      photoPreview.value = ''
      rectoPreview.value = ''
      versoPreview.value = ''
    }

    const handlePhotoUpload = (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        formData.photoProfil = file
        const reader = new FileReader()
        reader.onload = (e) => {
          photoPreview.value = e.target?.result as string
        }
        reader.readAsDataURL(file)
      }
    }

    const handleRectoUpload = (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        formData.pieceRecto = file
        const reader = new FileReader()
        reader.onload = (e) => {
          rectoPreview.value = e.target?.result as string
        }
        reader.readAsDataURL(file)
      }
    }

    const handleVersoUpload = (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        formData.pieceVerso = file
        const reader = new FileReader()
        reader.onload = (e) => {
          versoPreview.value = e.target?.result as string
        }
        reader.readAsDataURL(file)
      }
    }

    const handleBulletinUpload = (event: Event, bulletinNumber: number) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (file) {
        if (bulletinNumber === 1) formData.bulletin1 = file
        else if (bulletinNumber === 2) formData.bulletin2 = file
        else if (bulletinNumber === 3) formData.bulletin3 = file
      }
    }

    const goToNextStep = async () => {
      if (!formData.pieceIdentiteType || !formData.numeroPiece) {
        await showAlert.warning('Veuillez remplir tous les champs obligatoires')
        return
      }
      editStep.value = 2
    }

    const goToPreviousStep = () => {
      editStep.value = 1
    }

    const handleSubmitKyc = async () => {
      if (isSubmitting.value) return

      isSubmitting.value = true

      try {
        // Ici, vous devriez appeler une API pour mettre à jour les données KYC
        // Pour l'instant, on simule un succès
        await showAlert.success('Informations KYC mises à jour avec succès !')
        isEditing.value = false
        await loadKycData()
      } catch (error) {
        console.error('Erreur lors de la mise à jour des données KYC:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      } finally {
        isSubmitting.value = false
      }
    }

    const goBack = () => {
      router.push('/front/profil')
    }

    return {
      isEditing,
      editStep,
      isSubmitting,
      kycData,
      formData,
      photoPreview,
      rectoPreview,
      versoPreview,
      photoPreviewStyle,
      rectoPreviewStyle,
      versoPreviewStyle,
      formatPieceType,
      startEditing,
      cancelEditing,
      handlePhotoUpload,
      handleRectoUpload,
      handleVersoUpload,
      handleBulletinUpload,
      goToNextStep,
      goToPreviousStep,
      handleSubmitKyc,
      goBack
    }
  }
})
</script>

<style scoped>
.kyc-consultation {
  width: 100%;
}

.kyc-section {
  padding: 2rem 0;
  border-bottom: 1px solid rgba(var(--component-br-rgb), 0.2);
}

.kyc-section:last-child {
  border-bottom: none;
}

.kyc-photo-display {
  text-align: center;
}

.kyc-photo-preview {
  width: 150px;
  height: 150px;
  object-fit: cover;
}

.kyc-info-row {
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(var(--component-br-rgb), 0.1);
}

.kyc-label {
  font-weight: 600;
  color: var(--component-text);
}

.kyc-value {
  color: rgb(var(--component-text-rgb), 0.8);
}

.kyc-documents {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.kyc-document-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: rgb(var(--component-bg-rgb), 1);
  border: 1px solid rgba(var(--component-br-rgb), 0.2);
  border-radius: 10px;
}

.kyc-document-item span {
  flex: 1;
}

.kyc-doc-number {
  font-size: 1.5rem;
  font-weight: bold;
  color: rgb(var(--primary-color-rgb), 1);
  width: 40px;
}

.btn-sm {
  padding: 0.5rem 1rem;
  background-color: rgb(var(--primary-color-rgb), 1);
  color: white;
  border-radius: 10px;
  text-decoration: none;
  font-size: 0.9rem;
}

.btn-sm:hover {
  background-color: rgb(var(--primary-color-rgb), 0.8);
}

/* Dark Mode */
:root[data-theme="dark"] .kyc-section {
  border-bottom-color: rgba(var(--component-br-rgb), 0.3);
}

:root[data-theme="dark"] .kyc-info-row {
  border-bottom-color: rgba(var(--component-br-rgb), 0.2);
}

:root[data-theme="dark"] .kyc-document-item {
  background-color: rgb(var(--component-bg-rgb), 1);
  border-color: rgba(var(--component-br-rgb), 0.3);
}
</style>

